/**
 * +presence: other players of a shared field, drawn by the session's own renderer.
 *
 * Remote heroes go into the frame as ordinary `RenderUnit`s (kind 'player', faction 'party'), so
 * the toon renderer gives them exactly what the local hero gets: the class VRM (HeroView), run /
 * strafe / jump / cast poses, ability clips from animation cues, and per-ability VFX from synthetic
 * sim events through the existing FX table (begincast → hand glow, cast → release toward the
 * reported point, level_up → the golden pillar + cheer, a waystone jump → a poof at both ends and a
 * snap, never a slide: net/presenceModel.ts). Cast targets live in the remote player's
 * own sim, so the reported point becomes an invisible `frame.phantoms` unit the FX can aim at.
 *
 * Cost (≈30 players per shard):
 *  - only the nearest MAX_MODELS (6) players within VIEW_RANGE yd are drawn (others get no model,
 *    no nameplate); off-screen ones are frustum-culled by the renderer like any unit; they cast no
 *    shadow-map shadow (blob only), and beyond HALF_RATE_RANGE they animate every other frame and
 *    drop their outline hulls (RenderUnit.remote, render/toon/index.ts);
 *  - the server only streams players within FIELD_INTEREST_RANGE (90 yd), so a big zone map costs
 *    the same as a small field;
 *  - a VRM instance can't be cloned, so each drawn remote needs one parsed instance per class
 *    model: extra instances are parsed one at a time, then their geometry and textures are
 *    swapped for the first instance's (GPU memory stays ~one model per class), and pooled across
 *    maps by ToonAssets (released when a view is disposed);
 *  - nameplates are pooled DOM nodes moved with a transform, written only when they change.
 */
import type { ContentRegistry, SimEvent, UnitId } from '@keystone/sim';
import { JUMP_MS, MELEE_ABILITY_ID, jumpHeightAt } from '@keystone/sim';
import * as THREE from 'three';
import type { AnimCue, RenderFrame, Renderer, RenderUnit } from '../types';
import { OFFHAND_CUE_ID, castCueKind } from '../../game/frame'; // +autoattack: OFFHAND_CUE_ID
import { AN_AIR, AN_CAST, AN_CHANNEL, AN_GLIDE, AN_DEAD, type FieldEventOut } from '../../net/fieldProtocol';
import type { PresenceModel, RemotePlayer } from '../../net/presenceModel';
import { type ToonAssets, type VrmSlot, loadedToonAssets } from './assets';
import { heroVrmFile } from './units';
import { EMOTE_CHEER } from './anim/emotes';

/** Draw at most this many other players (nearest first); `?presencecap=N` overrides. */
export const MAX_MODELS = (() => {
  try {
    const n = Number(new URLSearchParams(location.search).get('presencecap'));
    return Number.isFinite(n) && n > 0 ? Math.min(40, Math.floor(n)) : 6;
  } catch {
    return 6;
  }
})();
/** Farther than this (yd), a drawn player animates every other frame (render/toon/index.ts). */
const HALF_RATE_RANGE = 22;
/** Players farther than this from the local hero are not drawn (yd); hysteresis keeps edge cases still. */
export const VIEW_RANGE = 60;
const VIEW_KEEP = 66;
/** Nameplates within this distance of the camera focus (yd). */
const PLATE_RANGE = 45;
/** How long a cast's VFX target point stays (projectiles fly to it). */
const PHANTOM_MS = 1600;

export const remoteUnitId = (id: number): UnitId => `~${id}`;

interface CastInfo {
  abilityId: string;
  start: number;
  dur: number;
}

interface Plate {
  root: HTMLElement;
  name: HTMLElement;
  lvl: HTMLElement;
  afk: HTMLElement;
  key: string;
  x: number;
  y: number;
  shown: boolean;
}

export class RemotePlayers {
  /** How many other players are drawn at most (MAX_MODELS; tools may change it at runtime). */
  cap = MAX_MODELS;
  /** Remote ids drawn last frame (the renderer holds a view for each). */
  private drawn = new Set<number>();
  private casts = new Map<number, CastInfo>();
  private jumps = new Map<number, number>();
  /** +glide: drawn heroes whose glider was open last frame. */
  private readonly gliding = new Map<UnitId, boolean>();
  private phantoms: { unit: RenderUnit; until: number }[] = [];
  private plates = new Map<number, Plate>();
  private readonly layer: HTMLElement;
  private assets: ToonAssets | null = null;
  private readonly assetsPending: boolean;
  private parsing = false;
  private readonly units = new Map<number, RenderUnit>();
  private disposed = false;

  constructor(hudHost: HTMLElement) {
    this.layer = document.createElement('div');
    this.layer.className = 'rp-layer';
    hudHost.appendChild(this.layer);
    injectStyle();
    const p = loadedToonAssets();
    this.assetsPending = !!p;
    void p?.then((a) => (this.assets = a)).catch(() => undefined);
  }

  /**
   * Add the drawn players to `frame` (units, phantom targets, synthetic events and cues) for
   * server time `t`. `events`: the presence events due this frame.
   */
  decorate(frame: RenderFrame, content: ContentRegistry, model: PresenceModel, t: number, events: readonly FieldEventOut[]): void {
    const me = frame.playerId ? frame.units.find((u) => u.id === frame.playerId) : undefined;
    const centre = me?.pos ?? null;
    // Who to draw: nearest first, inside the range (a little more for those already drawn).
    const cand: { p: RemotePlayer; d: number; pose: NonNullable<ReturnType<PresenceModel['pose']>> }[] = [];
    for (const p of model.players.values()) {
      const pose = model.pose(p, t);
      if (!pose) continue;
      const d = centre ? Math.hypot(pose.x - centre.x, pose.y - centre.y) : 0;
      if (d > (this.drawn.has(p.id) ? VIEW_KEEP : VIEW_RANGE)) continue;
      cand.push({ p, d, pose });
    }
    cand.sort((a, b) => (this.drawn.has(a.p.id) ? a.d - 4 : a.d) - (this.drawn.has(b.p.id) ? b.d - 4 : b.d));
    const next = new Set<number>();
    const want = new Map<string, number>();
    for (const c of cand) {
      if (next.size >= this.cap) break;
      if (!this.drawn.has(c.p.id) && !this.modelReady(c.p.cls, want)) continue;
      next.add(c.p.id);
      const u = this.unit(c.p, c.pose, content, t);
      u.remote = c.d > HALF_RATE_RANGE ? 2 : 1;
      frame.units.push(u);
    }
    this.drawn = next;
    for (const id of this.units.keys()) if (!model.players.has(id)) this.forget(id);

    // Events → the renderer's own event / cue paths.
    const evs = (frame.events ?? []) as SimEvent[];
    const cues: AnimCue[] = frame.cues ?? (frame.cues = []);
    const ts = frame.timeMs;
    for (const e of events) {
      const uid = remoteUnitId(e.id);
      if (e.k === 'cs') this.casts.set(e.id, { abilityId: e.a, start: t, dur: Math.max(1, e.d) });
      else if (e.k === 'cf' || e.k === 'cx') this.casts.delete(e.id);
      if (e.k === 'jump') this.jumps.set(e.id, t);
      // +presence: a waystone jump poofs at both ends even when the traveller is not drawn (it left
      // view, or arrives out of it); the renderer handles 'waystone' events (render/toon/index.ts).
      if (e.k === 'travel') {
        this.casts.delete(e.id);
        evs.push({ type: 'waystone', sourceID: uid, waystoneId: e.w, from: { x: e.from[0], y: e.from[1] }, to: { x: e.to[0], y: e.to[1] }, timestamp: ts } as SimEvent);
        continue;
      }
      if (!next.has(e.id)) continue;
      switch (e.k) {
        case 'cs':
          evs.push({ type: 'begincast', sourceID: uid, abilityGameID: e.a, duration: e.d, interruptible: false, timestamp: ts } as SimEvent);
          break;
        case 'cf': {
          let targetID: UnitId | undefined;
          const white = e.a === MELEE_ABILITY_ID || e.a === OFFHAND_CUE_ID; // +autoattack
          if (e.tp && !white) {
            targetID = `~t${e.id}_${(this.phantomSeq = (this.phantomSeq + 1) % 1000)}`;
            this.phantoms.push({ unit: phantom(targetID, e.tp[0], e.tp[1]), until: t + PHANTOM_MS });
          }
          if (white) {
            cues.push({ unitId: uid, kind: 'swing', abilityId: e.a });
            break;
          }
          evs.push({ type: 'cast', sourceID: uid, abilityGameID: e.a, ...(targetID ? { targetID } : {}), timestamp: ts } as SimEvent);
          cues.push({ unitId: uid, kind: castCueKind(content, e.a), abilityId: e.a });
          break;
        }
        case 'cx':
          evs.push({ type: 'cast_cancelled', sourceID: uid, abilityGameID: '', reason: 'player', timestamp: ts } as SimEvent);
          cues.push({ unitId: uid, kind: 'cancel' });
          break;
        case 'jump':
          evs.push({ type: 'jump', sourceID: uid, timestamp: ts } as SimEvent);
          cues.push({ unitId: uid, kind: 'spell', abilityId: 'common.jump' });
          break;
        case 'lvl':
          evs.push({ type: 'level_up', sourceID: uid, level: e.lvl, gained: 1, timestamp: ts } as SimEvent);
          break;
        case 'emote':
          if (e.a === 'cheer') cues.push({ unitId: uid, kind: 'spell', abilityId: EMOTE_CHEER });
          break;
        default:
          break;
      }
    }
    // +glide: the canopy opening / folding on another hero: the same 'glide' event (fwump, sounds).
    for (const u of frame.units) {
      if (!u.remote) continue;
      const was = this.gliding.get(u.id) ?? false;
      const now = !!u.glide;
      if (was !== now) evs.push({ type: 'glide', sourceID: u.id, on: now, timestamp: ts } as SimEvent);
      if (now) this.gliding.set(u.id, true);
      else this.gliding.delete(u.id);
    }
    frame.events = evs;
    this.phantoms = this.phantoms.filter((p) => p.until > t);
    if (this.phantoms.length) frame.phantoms = [...(frame.phantoms ?? []), ...this.phantoms.map((p) => p.unit)];
  }

  private phantomSeq = 0;

  private unit(p: RemotePlayer, pose: NonNullable<ReturnType<PresenceModel['pose']>>, content: ContentRegistry, t: number): RenderUnit {
    let u = this.units.get(p.id);
    const cls = content.class(p.cls);
    if (!u) {
      u = {
        id: remoteUnitId(p.id),
        name: p.name,
        kind: 'player',
        faction: 'party',
        role: cls?.role ?? 'dps',
        tier: null,
        pos: { x: pose.x, y: pose.y },
        facing: pose.f,
        radius: 0.5,
        alive: true,
        hpFrac: 1,
        color: cls?.color ?? '#e6e9ef',
        isPlayer: false,
        casting: false,
        classId: p.cls,
        npcId: null,
        vel: { x: 0, y: 0 },
      };
      this.units.set(p.id, u);
    }
    u.name = p.name;
    u.pos.x = pose.x;
    u.pos.y = pose.y;
    u.facing = pose.f;
    u.alive = !(pose.an & AN_DEAD);
    u.vel!.x = pose.vx;
    u.vel!.y = pose.vy;
    u.speed = Math.hypot(pose.vx, pose.vy);
    u.level = p.lvl;
    u.inCombat = pose.tk === 'e';
    const casting = !!(pose.an & (AN_CAST | AN_CHANNEL)) && !!pose.c;
    u.casting = casting;
    if (casting) {
      const ci = this.casts.get(p.id);
      const progress = ci && ci.abilityId === pose.c ? Math.min(1, (t - ci.start) / ci.dur) : 0.5;
      const school = content.ability(pose.c!)?.school ?? null;
      u.cast = { kind: pose.an & AN_CHANNEL ? 'channel' : 'cast', school, progress: pose.an & AN_CHANNEL ? 1 - progress : progress, abilityId: pose.c! };
    } else u.cast = null;
    const j = this.jumps.get(p.id);
    u.jumpH = j !== undefined ? jumpHeightAt({ at: j, until: j + JUMP_MS }, t) : 0;
    if (j !== undefined && t > j + JUMP_MS) this.jumps.delete(p.id);
    // +glide: another hero in the air / on a platform: its height (the renderer lifts it), the canopy.
    if (pose.z !== undefined) u.z = pose.z;
    else delete u.z;
    u.glide = !!(pose.an & AN_GLIDE);
    u.perched = pose.z !== undefined && !(pose.an & AN_AIR);
    return u;
  }

  private forget(id: number): void {
    this.units.delete(id);
    this.casts.delete(id);
    this.jumps.delete(id);
    const pl = this.plates.get(id);
    if (pl) pl.root.remove();
    this.plates.delete(id);
  }

  /**
   * Is a VRM instance free for a new remote of class `cls`? (`want` counts the new ones this
   * frame per file.) When not, parse one more instance in the background.
   */
  private modelReady(cls: string, want: Map<string, number>): boolean {
    if (!this.assetsPending) return true; // no toon assets (placeholder renderer): capsules
    const a = this.assets;
    if (!a) return false;
    const file = heroVrmFile({ classId: cls, role: null });
    if (!file || a.failed.has(`vrm:${file}`) || !a.vrmBuffers.has(file)) return true; // capsule fallback
    const free = (a.vrms.get(file) ?? []).filter((s) => !s.inUse).length;
    const n = want.get(file) ?? 0;
    if (free > n) {
      want.set(file, n + 1);
      return true;
    }
    this.parseOne(a, file);
    return false;
  }

  private parseOne(a: ToonAssets, file: string): void {
    if (this.parsing || this.disposed) return;
    this.parsing = true;
    const first = (a.vrms.get(file) ?? [])[0] ?? null;
    void a
      .acquireVrm(file)
      .then((slot) => {
        if (!slot) return;
        if (first && slot !== first) shareModel(first, slot);
        a.releaseVrm(slot);
      })
      .catch((err) => console.warn('[presence] could not load a hero model', err))
      .finally(() => {
        this.parsing = false;
      });
  }

  /** Nameplates over the drawn players (after the renderer placed the camera). */
  overlay(renderer: Renderer, focus: { x: number; y: number }, model: PresenceModel, content: ContentRegistry | null): void {
    for (const [id, pl] of this.plates) {
      const u = this.units.get(id);
      const p = model.players.get(id);
      let show = false;
      if (u && p && this.drawn.has(id) && Math.hypot(u.pos.x - focus.x, u.pos.y - focus.y) <= PLATE_RANGE) {
        const sp = renderer.project(u.pos, renderer.unitHeight(u.id) + 0.12 + (u.jumpH ?? 0)); // under the speech bubble (+0.5)
        if (sp.visible) {
          show = true;
          const x = Math.round(sp.x);
          const y = Math.round(sp.y);
          if (x !== pl.x || y !== pl.y) {
            pl.x = x;
            pl.y = y;
            pl.root.style.transform = `translate3d(${x}px,${y}px,0) translate(-50%,-100%)`;
          }
          this.fill(pl, p, content);
        }
      }
      if (show !== pl.shown) {
        pl.shown = show;
        pl.root.style.display = show ? '' : 'none';
      }
    }
    for (const id of this.drawn) {
      if (this.plates.has(id)) continue;
      const pl = this.makePlate();
      this.plates.set(id, pl);
    }
  }

  private fill(pl: Plate, p: RemotePlayer, content: ContentRegistry | null): void {
    const key = `${p.name}|${p.lvl}|${p.afk}|${p.cls}`;
    if (key === pl.key) return;
    pl.key = key;
    pl.name.textContent = p.name;
    pl.name.style.color = content?.class(p.cls)?.color ?? '#e6e9ef';
    pl.lvl.textContent = String(p.lvl);
    pl.afk.style.display = p.afk ? '' : 'none';
  }

  private makePlate(): Plate {
    const root = document.createElement('div');
    root.className = 'rp-plate';
    root.style.display = 'none';
    const lvl = document.createElement('span');
    lvl.className = 'rp-lvl';
    const name = document.createElement('span');
    name.className = 'rp-name';
    const afk = document.createElement('span');
    afk.className = 'rp-afk';
    afk.textContent = 'AFK';
    afk.style.display = 'none';
    root.append(lvl, name, afk);
    this.layer.appendChild(root);
    return { root, name, lvl, afk, key: '', x: NaN, y: NaN, shown: false };
  }

  dispose(): void {
    this.disposed = true;
    this.layer.remove();
    this.plates.clear();
    this.units.clear();
    this.drawn.clear();
  }
}

function phantom(id: UnitId, x: number, y: number): RenderUnit {
  return { id, name: '', kind: 'npc', faction: 'enemy', role: null, tier: null, pos: { x, y }, facing: 0, radius: 0.5, alive: true, hpFrac: 1, color: '#000', isPlayer: false, casting: false };
}

/**
 * Point a freshly parsed VRM instance at the first instance's geometry and textures (same file,
 * same structure) and free its own copies: an extra player of a class costs a skeleton and
 * material instances, not another ~40 MB of textures. Materials stay per instance (hit flashes
 * and the self-fade change them). Any mismatch leaves the instance as parsed.
 */
export function shareModel(src: VrmSlot, dst: VrmSlot): void {
  try {
    const meshes = (s: VrmSlot): THREE.Mesh[] => {
      const out: THREE.Mesh[] = [];
      s.vrm.scene.traverse((o) => {
        if ((o as THREE.Mesh).isMesh && !o.userData['hull']) out.push(o as THREE.Mesh);
      });
      return out;
    };
    const a = meshes(src);
    const b = meshes(dst);
    if (a.length !== b.length) return;
    const dropTex = new Set<THREE.Texture>();
    const keepTex = new Set<THREE.Texture>();
    for (let i = 0; i < a.length; i++) {
      const ma = a[i]!;
      const mb = b[i]!;
      const ga = ma.geometry;
      const gb = mb.geometry;
      const pa = ga.attributes['position'];
      const pb = gb.attributes['position'];
      if (pa && pb && pa.count === pb.count && (ga.index?.count ?? 0) === (gb.index?.count ?? 0) && ga !== gb) {
        mb.geometry = ga;
        gb.dispose();
      }
      const mats = (m: THREE.Mesh): THREE.Material[] => (Array.isArray(m.material) ? m.material : [m.material]);
      const xa = mats(ma);
      const xb = mats(mb);
      if (xa.length !== xb.length) continue;
      for (let k = 0; k < xa.length; k++) swapTextures(xa[k]!, xb[k]!, dropTex, keepTex);
    }
    for (const t of dropTex) {
      if (keepTex.has(t)) continue;
      t.dispose();
      const img = t.image as { close?: () => void } | null;
      img?.close?.();
    }
  } catch (err) {
    console.warn('[presence] model sharing skipped', err);
  }
}

function swapTextures(from: THREE.Material, to: THREE.Material, drop: Set<THREE.Texture>, keep: Set<THREE.Texture>): void {
  const rec = to as unknown as Record<string, unknown>;
  const src = from as unknown as Record<string, unknown>;
  for (const k of Object.keys(rec)) {
    const v = rec[k];
    const s = src[k];
    if (v instanceof THREE.Texture && s instanceof THREE.Texture && v !== s) {
      rec[k] = s;
      drop.add(v);
      keep.add(s);
    }
  }
  const ua = (from as THREE.ShaderMaterial).uniforms;
  const ub = (to as THREE.ShaderMaterial).uniforms;
  if (ua && ub) {
    for (const k of Object.keys(ub)) {
      const v = ub[k]?.value;
      const s = ua[k]?.value;
      if (v instanceof THREE.Texture && s instanceof THREE.Texture && v !== s) {
        ub[k]!.value = s;
        drop.add(v);
        keep.add(s);
      }
    }
  }
  to.needsUpdate = true;
}

let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const s = document.createElement('style');
  s.textContent = `
.rp-layer{position:absolute;inset:0;pointer-events:none;overflow:hidden;z-index:2}
.rp-plate{position:absolute;left:0;top:0;white-space:nowrap;font:600 12px/1.2 var(--hk-font, system-ui, sans-serif);
  text-shadow:0 1px 2px #000,0 0 3px #000;display:flex;gap:5px;align-items:center;will-change:transform}
.rp-lvl{font-size:10px;color:#f5e6b8;background:rgba(10,12,20,.65);border:1px solid rgba(245,230,184,.45);border-radius:7px;padding:0 4px;line-height:13px}
.rp-afk{font-size:10px;color:#c9ced8;background:rgba(10,12,20,.6);border-radius:3px;padding:0 3px}
`;
  document.head.appendChild(s);
}
